import { Flex } from '@chakra-ui/react'
import type { CampaignEntity } from '@twlvxtwlv/cms'
import { type ComponentCampaignCurrentNft } from '@twlvxtwlv/cms'
import type { CurrentNftWithProduct } from '@twlvxtwlv/mapping'
import type { AuctionWithHighestBid } from '@twlvxtwlv/supabase'
import type { ArtistWithProducts, Product } from '@twlvxtwlv/tokenforge'
import type {
  CampaignDetailsSlugType,
  CampaignNavigationRecords,
  NotifyMeAttributes,
  RoadmapEntry,
  SalesType,
} from '@twlvxtwlv/types'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { Card } from '../../../../shared/cards/card'
import { ExpandableCard } from '../../../../shared/cards/expandable-card'
import { ExternalNavigationButton } from '../../../../shared/external-navigation-button'
import { SalesTypeIcon } from '../../../../shared/icons/sales-type'
import { About } from '../shared/about'
import { CampaignHero } from '../shared/campaign-hero'
import { ProductDetails } from '../shared/product-details'
import { RoadmapList } from '../shared/roadmap-list'

interface CampaignDetailsMobileProps {
  artistsWithProducts: ArtistWithProducts[]
  auctionsWithHighestBid: AuctionWithHighestBid[]
  campaignContainsAward: boolean
  campaignEntity: CampaignEntity
  campaignNavigationRecords: CampaignNavigationRecords
  currentNftsWithProducts: CurrentNftWithProduct[]
  onNotifyMeAttributesChanged: (attributes: NotifyMeAttributes | null) => void
  onOpenNotifyMeModal: () => void
  onTrackLearnMoreButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  onTrackNavigation: (slug: CampaignDetailsSlugType, product: Product | undefined | null) => void
  onTrackPurchaseButtonClicked: (
    currentNft: ComponentCampaignCurrentNft,
    priceInEuroCents: number
  ) => void
  roadmapEntries: RoadmapEntry[]
}

export function CampaignDetailsMobile({
  artistsWithProducts,
  auctionsWithHighestBid,
  campaignContainsAward,
  campaignEntity,
  campaignNavigationRecords,
  currentNftsWithProducts,
  onNotifyMeAttributesChanged,
  onOpenNotifyMeModal,
  onTrackLearnMoreButtonClicked,
  onTrackNavigation,
  onTrackPurchaseButtonClicked,
  roadmapEntries,
}: CampaignDetailsMobileProps) {
  const { t } = useTranslation('campaign')
  const router = useRouter()

  const contents = campaignEntity.attributes?.campaignContents?.contents

  function handleTrackNavigation(
    slug: CampaignDetailsSlugType,
    product: Product | undefined | null
  ) {
    onTrackNavigation(slug, product)
  }

  function handleOnCardToggle(slug: CampaignDetailsSlugType, product: Product) {
    handleTrackNavigation(slug, product)

    const campaignId = campaignEntity.attributes?.tokenForge?.campaignId
    if (campaignId) {
      let path = `/campaign/${campaignId}/?show=${slug}`
      if (slug === 'token') {
        path += `&product-id=${product.code}`
      }
      router.replace(path, undefined, {
        shallow: true,
      })
    }
  }

  return (
    <Flex direction="column" w="full" h="full" gap={2}>
      <Card
        backgroundColor={campaignNavigationRecords.artist.backgroundColor}
        isMinFullHeight={false}
      >
        <CampaignHero
          artist={artistsWithProducts[0]?.artist ?? null}
          campaignEntity={campaignEntity}
          isAwardTeaser={campaignContainsAward}
          isLabelTeaser
        />
      </Card>

      {contents && contents.length > 0 ? (
        <ExpandableCard
          isExpanded={router.query.show === 'about'}
          title={t(campaignNavigationRecords.about.titleTranslationKey ?? '')}
          backgroundColor={campaignNavigationRecords.about.backgroundColor}
          onCardToggle={() =>
            handleOnCardToggle('about', currentNftsWithProducts[0]?.product ?? null)
          }
        >
          <About campaignEntity={campaignEntity} />
        </ExpandableCard>
      ) : null}

      {currentNftsWithProducts
        ? currentNftsWithProducts.map((currentNftWithProduct) => {
            const key = `token-${currentNftWithProduct.product.code}`
            const navItem = campaignNavigationRecords[key]
            const isExpanded =
              router.query.show === 'token' &&
              currentNftWithProduct.product.code === router.query['product-id']
            return (
              <ExpandableCard
                key={currentNftWithProduct.currentNft.id}
                isExpanded={isExpanded}
                title={currentNftWithProduct.currentNft.title ?? ''}
                titleLeftElement={navItem.titleLeftElement}
                titleRightElement={
                  <SalesTypeIcon salesType={currentNftWithProduct.currentNft.type as SalesType} />
                }
                backgroundColor="white"
                onCardToggle={() => handleOnCardToggle('token', currentNftWithProduct.product)}
              >
                <ProductDetails
                  auctionsWithHighestBid={auctionsWithHighestBid}
                  campaignId={campaignEntity.attributes?.tokenForge?.campaignId ?? null}
                  currentNftWithProduct={currentNftWithProduct}
                  onOpenNotifyMeModal={onOpenNotifyMeModal}
                  onTrackLearnMoreButtonClicked={onTrackLearnMoreButtonClicked}
                  onTrackPurchaseButtonClicked={onTrackPurchaseButtonClicked}
                />
              </ExpandableCard>
            )
          })
        : null}

      {roadmapEntries && roadmapEntries.length > 0 && (
        <ExpandableCard
          isExpanded={router.query.show === 'roadmap'}
          title={t(campaignNavigationRecords.roadmap.titleTranslationKey ?? '')}
          backgroundColor={campaignNavigationRecords.roadmap.backgroundColor}
          onCardToggle={() =>
            handleOnCardToggle('roadmap', currentNftsWithProducts[0]?.product ?? null)
          }
        >
          <RoadmapList
            auctionsWithHighestBid={auctionsWithHighestBid}
            items={roadmapEntries}
            onNotifyMeAttributesChanged={onNotifyMeAttributesChanged}
            onOpenNotifyMeModal={onOpenNotifyMeModal}
          />
        </ExpandableCard>
      )}

      {campaignEntity.id === '3' ||
      campaignEntity.id === '10' ||
      campaignEntity.id === '25' ||
      campaignEntity.id === '26' ? ( // IDs for Campaign DIE DREI ???
        <ExternalNavigationButton url="https://bit.ly/ddf-faq" buttonText="FAQs" />
      ) : null}
    </Flex>
  )
}
